import { motion, AnimatePresence } from "framer-motion";
import { springPresets, staggerContainer, staggerItem } from "@/lib/motion";
import { Phone, MapPin, Globe, Menu, X, ChevronDown, Wrench, Shield, Star, Clock } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SERVICES, WHY_US, CONTACT, ZONES, FAQS } from "@/data/index";

// ─── Smooth scroll helper ─────────────────────────────────────────────────────
function scrollTo(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

// ─── Cookie Toggle Switch ─────────────────────────────────────────────────────
function CookieSwitch({ enabled, onChange, disabled = false }: { enabled: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => !disabled && onChange(!enabled)}
      className="relative flex-shrink-0 w-10 h-5 rounded-full transition-all duration-300 focus:outline-none"
      style={{
        background: enabled ? "#DAB463" : "#cbd5d4",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <motion.span
        animate={{ x: enabled ? 20 : 2 }}
        transition={springPresets.snappy}
        className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow"
        style={{ display: "block" }}
      />
    </button>
  );
}

// ─── Cookie Config Modal ──────────────────────────────────────────────────────
function CookieConfigModal({
  onSave,
  onClose,
}: {
  onSave: (prefs: Record<string, boolean>) => void;
  onClose: () => void;
}) {
  const [prefs, setPrefs] = useState({ preferencias: false, estadisticas: false, marketing: false });

  const categories = [
    {
      key: "necesarias",
      label: "Necesarias",
      desc: "Imprescindibles para el funcionamiento del sitio web. No pueden desactivarse.",
      always: true,
    },
    {
      key: "preferencias",
      label: "Preferencias",
      desc: "Permiten recordar información que cambia el comportamiento o aspecto del sitio (idioma, región, etc.).",
      always: false,
    },
    {
      key: "estadisticas",
      label: "Estadísticas",
      desc: "Nos ayudan a entender cómo interactúan los visitantes con el sitio web mediante la recopilación anónima de datos.",
      always: false,
    },
    {
      key: "marketing",
      label: "Marketing",
      desc: "Se usan para rastrear a los visitantes entre sitios web y mostrar anuncios relevantes y atractivos.",
      always: false,
    },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.6)" }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 20 }}
          transition={springPresets.gentle}
          className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
          style={{ background: "#fff" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4" style={{ background: "#295F5A" }}>
            <div>
              <h2 className="text-white font-bold text-base">🍪 Configurar cookies</h2>
              <p className="text-white/60 text-xs mt-0.5">Activa o desactiva cada categoría</p>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
              <X size={20} />
            </button>
          </div>

          {/* Categories */}
          <div className="divide-y" style={{ borderColor: "rgba(41,95,90,0.1)" }}>
            {categories.map((cat) => (
              <div key={cat.key} className="flex items-start gap-4 px-6 py-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-bold" style={{ color: "#295F5A" }}>{cat.label}</span>
                    {cat.always && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: "rgba(41,95,90,0.1)", color: "#295F5A" }}>
                        Siempre activas
                      </span>
                    )}
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: "#666" }}>{cat.desc}</p>
                </div>
                <CookieSwitch
                  enabled={cat.always ? true : prefs[cat.key as keyof typeof prefs]}
                  onChange={(v) => setPrefs({ ...prefs, [cat.key]: v })}
                  disabled={cat.always}
                />
              </div>
            ))}
          </div>

          {/* Footer buttons */}
          <div className="flex gap-3 px-6 py-4" style={{ borderTop: "1px solid rgba(41,95,90,0.1)" }}>
            <button
              onClick={() => onSave({ preferencias: false, estadisticas: false, marketing: false })}
              className="flex-1 py-2.5 rounded-lg text-xs font-semibold border transition-all"
              style={{ borderColor: "rgba(41,95,90,0.3)", color: "#295F5A" }}
            >
              Rechazar opcionales
            </button>
            <button
              onClick={() => onSave(prefs)}
              className="flex-1 py-2.5 rounded-lg text-xs font-bold transition-all"
              style={{ background: "#295F5A", color: "#fff" }}
            >
              Guardar preferencias
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Cookie Banner ────────────────────────────────────────────────────────────
function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) setVisible(true);
  }, []);

  const accept = () => {
    localStorage.setItem("cookie_consent", "accepted");
    setVisible(false);
  };
  const reject = () => {
    localStorage.setItem("cookie_consent", "rejected");
    setVisible(false);
  };
  const saveConfig = (prefs: Record<string, boolean>) => {
    localStorage.setItem("cookie_consent", "custom");
    localStorage.setItem("cookie_prefs", JSON.stringify(prefs));
    setShowConfig(false);
    setVisible(false);
  };

  return (
    <>
      {/* Config Modal */}
      {showConfig && <CookieConfigModal onSave={saveConfig} onClose={() => setShowConfig(false)} />}

      {/* Banner */}
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={springPresets.gentle}
            className="fixed bottom-0 left-0 right-0 z-[100] p-4"
            role="dialog"
            aria-label="Aviso de cookies"
          >
            <div
              className="max-w-7xl mx-auto rounded-xl px-4 py-3 shadow-2xl flex flex-col md:flex-row items-start md:items-center gap-3"
              style={{ background: "#1e4a46", border: "1px solid rgba(218,180,99,0.4)" }}
            >
              <div className="flex-1">
                <p className="text-white text-xs leading-snug">
                  🍪 Utilizamos cookies propias y de terceros para analizar el uso del sitio web y mostrarte publicidad relacionada con tus preferencias sobre la base de un perfil elaborado a partir de tus hábitos de navegación (por ejemplo, páginas visitadas). Puedes aceptar el uso de todas las cookies pulsando el botón <strong style={{ color: "#DAB463" }}>ACEPTAR</strong>, para rechazar o configurar puedes pulsar el botón <strong style={{ color: "#71C2BB" }}>CONFIGURAR</strong> y para rechazar todas las cookies opcionales pulsa el botón <strong style={{ color: "rgba(255,255,255,0.8)" }}>RECHAZAR</strong>. Para más información puedes visitar nuestra{" "}
                  <Link to="/politica-cookies" className="underline transition-colors font-semibold" style={{ color: "#71C2BB" }}>
                    política de cookies
                  </Link>
                  .
                </p>
              </div>
              <div className="flex flex-row gap-2 flex-shrink-0">
                <button
                  onClick={reject}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold border transition-all whitespace-nowrap"
                  style={{ borderColor: "rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.7)" }}
                >
                  RECHAZAR
                </button>
                <button
                  onClick={() => setShowConfig(true)}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold border transition-all whitespace-nowrap"
                  style={{ borderColor: "#71C2BB", color: "#71C2BB" }}
                >
                  CONFIGURAR
                </button>
                <button
                  onClick={accept}
                  className="px-4 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap"
                  style={{ background: "#DAB463", color: "#1e4a46" }}
                >
                  ACEPTAR
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── Header / Nav ────────────────────────────────────────────────────────────
function Header() {
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = [
    { id: "servicios", label: "Servicios" },
    { id: "nosotros", label: "Nosotros" },
    { id: "contacto", label: "Contacto" },
  ];

  const handleNav = (id: string) => {
    setMenuOpen(false);
    scrollTo(id);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-primary shadow-lg">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        <button onClick={() => scrollTo("inicio")} className="flex items-center gap-3">
          <img
            src="/images/logo.png"
            alt="Logo Reparaciones y Reformas Oscar Alaminos"
            className="h-14 w-auto object-contain"
          />
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => handleNav(link.id)}
              className="text-primary-foreground/80 hover:text-accent font-medium text-sm uppercase tracking-wider transition-colors duration-200 bg-transparent border-none cursor-pointer"
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => handleNav("contacto")}
            className="bg-accent text-accent-foreground px-5 py-2 rounded-md font-bold text-sm uppercase tracking-wider hover:bg-accent/80 transition-all duration-200 border-none cursor-pointer"
          >
            Presupuesto Gratis
          </button>
        </nav>

        {/* Mobile hamburger */}
        <button
          className="md:hidden text-primary-foreground"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Abrir menú"
        >
          {menuOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={springPresets.snappy}
            className="md:hidden bg-primary border-t border-primary-foreground/10 px-4 pb-5"
          >
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => handleNav(link.id)}
                className="block w-full text-left py-3 text-primary-foreground/80 hover:text-accent font-medium text-sm uppercase tracking-wider border-b border-primary-foreground/10 bg-transparent border-l-0 border-r-0 border-t-0 cursor-pointer"
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => handleNav("contacto")}
              className="block w-full mt-4 bg-accent text-accent-foreground px-5 py-3 rounded-md font-bold text-sm uppercase text-center cursor-pointer border-none"
            >
              Presupuesto Gratis
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// ─── Hero Section ─────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section
      id="inicio"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: "linear-gradient(135deg, #295F5A 0%, #1e4a46 60%, #162f2d 100%)" }}
    >
      {/* Decorative ruler lines */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <div key={i} className="absolute border-t border-white/40" style={{ top: `${i * 5.5}%`, left: 0, right: 0 }} />
        ))}
      </div>

      {/* Accent bars */}
      <div className="absolute left-0 top-0 h-full w-1.5 opacity-70" style={{ background: "#DAB463" }} />
      <div className="absolute right-0 top-0 h-full w-1.5 opacity-40" style={{ background: "#71C2BB" }} />

      <div className="container mx-auto px-6 text-center relative z-10 pt-24">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={springPresets.gentle}>
          <div className="flex justify-center mb-8">
            <img
              src="/images/logo.png"
              alt="Reparaciones y Reformas Oscar Alaminos - Empresa de reformas en Motril Granada Costa Tropical"
              className="h-36 md:h-48 w-auto object-contain drop-shadow-2xl"
            />
          </div>

          <div
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-6"
            style={{ background: "rgba(218,180,99,0.18)", color: "#DAB463", border: "1px solid #DAB463" }}
          >
            Torrenueva Costa · Granada
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-4">
            Reformas y Reparaciones en{" "}
            <span style={{ color: "#DAB463" }}>Motril y Granada</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Empresa de reformas integrales, albañilería, fontanería y reparaciones urgentes en Motril, Granada y Costa Tropical. Colaboramos con aseguradoras para siniestros en vivienda.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.div
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold text-base shadow-lg select-none cursor-default"
              style={{ background: "#DAB463", color: "#1e4a46" }}
            >
              <Phone size={18} /> Llámanos al 623 624 182
            </motion.div>
            <motion.button
              onClick={() => scrollTo("servicios")}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold text-base uppercase tracking-wide border-2 cursor-pointer bg-transparent"
              style={{ borderColor: "#71C2BB", color: "#71C2BB" }}
            >
              Ver servicios <ChevronDown size={18} />
            </motion.button>
          </div>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.3 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { value: "+5", label: "Años de experiencia" },
            { value: "+150", label: "Proyectos realizados" },
            { value: "100%", label: "Proyectos con garantía" },
            { value: "24h", label: "Respuesta urgencias" },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl px-4 py-5"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
            >
              <div className="text-3xl font-extrabold" style={{ color: "#DAB463" }}>{stat.value}</div>
              <div className="text-white/60 text-xs mt-1 uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── Services Section ─────────────────────────────────────────────────────────
function Services() {
  return (
    <section id="servicios" className="py-24 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={springPresets.gentle}
          className="text-center mb-16"
        >
          <div
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-4"
            style={{ background: "rgba(41,95,90,0.1)", color: "#295F5A", border: "1px solid #295F5A" }}
          >
            Nuestros Servicios
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-foreground mb-4">Servicios de Reformas y Reparaciones en Granada</h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Ofrecemos soluciones completas de reforma y reparación de viviendas en Motril, Granada y toda la Costa Tropical.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {SERVICES.map((service) => (
            <motion.div
              key={service.id} variants={staggerItem}
              whileHover={{ y: -6, transition: springPresets.snappy }}
              className="rounded-2xl p-8 cursor-default group"
              style={{ background: "#fff", border: "1px solid rgba(41,95,90,0.12)", boxShadow: "0 4px 24px -4px rgba(41,95,90,0.08)" }}
            >
              <div
                className="text-4xl mb-5 w-14 h-14 flex items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-110"
                style={{ background: "rgba(41,95,90,0.08)" }}
              >{service.icon}</div>
              <h3 className="text-xl font-bold mb-2" style={{ color: "#295F5A" }}>{service.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{service.description}</p>
              <div className="mt-5 h-0.5 w-8 rounded-full transition-all duration-300 group-hover:w-16" style={{ background: "#DAB463" }} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── Why Us Section ───────────────────────────────────────────────────────────
function WhyUs() {
  return (
    <section id="nosotros" className="py-24" style={{ background: "linear-gradient(135deg, #295F5A 0%, #1e4a46 100%)" }}>
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={springPresets.gentle}
          className="text-center mb-16"
        >
          <div
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-4"
            style={{ background: "rgba(218,180,99,0.15)", color: "#DAB463", border: "1px solid #DAB463" }}
          >¿Por qué elegirnos?</div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">Compromiso con la calidad</h2>
          <p className="text-white/65 text-lg max-w-xl mx-auto">Nuestra reputación se construye trabajo a trabajo, cliente a cliente.</p>
        </motion.div>

        <motion.div
          variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {WHY_US.map((item) => (
            <motion.div
              key={item.title} variants={staggerItem}
              className="flex gap-5 p-7 rounded-2xl"
              style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)" }}
            >
              <div className="text-3xl w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-xl" style={{ background: "rgba(218,180,99,0.18)" }}>
                {item.icon}
              </div>
              <div>
                <h3 className="text-white font-bold text-lg mb-1">{item.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }} transition={springPresets.gentle}
          className="mt-14 text-center py-8 px-6 rounded-2xl"
          style={{ background: "rgba(218,180,99,0.12)", border: "1px solid #DAB463" }}
        >
          <p className="text-2xl font-bold italic" style={{ color: "#DAB463" }}>"Calidad y Compromiso en Cada Proyecto"</p>
          <p className="text-white/60 text-sm mt-2 tracking-widest uppercase">— Reparaciones y Reformas Oscar Alaminos</p>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Insurance Section ────────────────────────────────────────────────────────
function InsuranceSection() {
  const steps = [
    { icon: <Phone size={22} />, title: "Nos llamas", desc: "Contáctanos y nos desplazamos a valorar el siniestro sin coste." },
    { icon: <Shield size={22} />, title: "Gestionamos con tu aseguradora", desc: "Coordinamos directamente con la compañía de seguros toda la documentación." },
    { icon: <Wrench size={22} />, title: "Ejecutamos la reparación", desc: "Realizamos el trabajo con las máximas garantías de calidad." },
    { icon: <Star size={22} />, title: "Entrega y garantía", desc: "Te entregamos el trabajo acabado con garantía escrita sobre la reparación." },
  ];
  return (
    <section className="py-24 bg-muted/40">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={springPresets.gentle}
          className="text-center mb-16"
        >
          <div
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-4"
            style={{ background: "rgba(113,194,187,0.15)", color: "#71C2BB", border: "1px solid #71C2BB" }}
          >Aseguradoras</div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-foreground mb-4">Reparaciones de siniestros</h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Trabajamos con las principales compañías aseguradoras para que tú no tengas que preocuparte de nada.
          </p>
        </motion.div>
        <div className="relative">
          <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5" style={{ background: "linear-gradient(90deg, #DAB463, #71C2BB)" }} />
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <motion.div key={step.title} variants={staggerItem} className="text-center relative">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg"
                  style={{ background: i % 2 === 0 ? "#295F5A" : "#DAB463", color: i % 2 === 0 ? "#fff" : "#1e4a46" }}
                >{step.icon}</div>
                <h3 className="font-bold text-foreground text-base mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── Contact Section ──────────────────────────────────────────────────────────
function Contact() {
  const tiposServicio = [
    { icon: "🧱", label: "Albañilería y obra" },
    { icon: "🔧", label: "Fontanería" },
    { icon: "🏠", label: "Reforma integral" },
    { icon: "🛡️", label: "Siniestro de aseguradora" },
    { icon: "🪟", label: "Revestimientos y acabados" },
    { icon: "⚡", label: "Reparación urgente" },
  ];

  return (
    <section id="contacto" className="py-24 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={springPresets.gentle}
          className="text-center mb-16"
        >
          <div
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-4"
            style={{ background: "rgba(41,95,90,0.1)", color: "#295F5A", border: "1px solid #295F5A" }}
          >Contacto</div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-foreground mb-4">¿Hablamos de tu proyecto?</h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Solicita un presupuesto sin compromiso. Te respondemos en menos de 24 horas.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">

          {/* Columna izquierda — Datos de contacto */}
          <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={springPresets.gentle}>
            <div className="rounded-2xl p-8 h-full" style={{ background: "linear-gradient(135deg, #295F5A, #1e4a46)", color: "#fff" }}>
              <img src="/images/logo.png" alt="Reformas Oscar Alaminos Motril Granada" className="h-20 w-auto object-contain mb-6" />
              <h3 className="text-xl font-bold mb-6" style={{ color: "#DAB463" }}>{CONTACT.name}</h3>
              <div className="space-y-4">
                {/* Teléfono */}
                <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 group">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(218,180,99,0.2)" }}>
                    <Phone size={18} style={{ color: "#DAB463" }} />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider">Teléfono</p>
                    <p className="text-white font-mono font-semibold group-hover:text-accent transition-colors">{CONTACT.phone}</p>
                  </div>
                </a>
                {/* Email */}
                <a href="mailto:presupuestos@reformasoscaralaminos.es" className="flex items-center gap-3 group">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(218,180,99,0.2)" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DAB463" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider">Email presupuestos</p>
                    <p className="text-white text-sm font-semibold group-hover:text-accent transition-colors break-all">presupuestos@reformasoscaralaminos.es</p>
                  </div>
                </a>
                {/* Dirección */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: "rgba(113,194,187,0.2)" }}>
                    <MapPin size={18} style={{ color: "#71C2BB" }} />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider">Dirección</p>
                    <p className="text-white text-sm leading-relaxed">{CONTACT.address}</p>
                  </div>
                </div>
                {/* Web */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(218,180,99,0.2)" }}>
                    <Globe size={18} style={{ color: "#DAB463" }} />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider">Web</p>
                    <a href={`https://${CONTACT.web}`} className="text-white text-sm hover:text-accent transition-colors">{CONTACT.web}</a>
                  </div>
                </div>
                {/* Horario */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(113,194,187,0.2)" }}>
                    <Clock size={18} style={{ color: "#71C2BB" }} />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider">Horario</p>
                    <p className="text-white text-sm">Lun–Vie: 8:00–20:00 · Urgencias 24h</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Columna derecha — Email + tipos de trabajo */}
          <motion.div initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={springPresets.gentle}
            className="flex flex-col gap-6"
          >
            {/* Tarjeta email destacado */}
            <div
              className="rounded-2xl p-8 text-center"
              style={{ background: "#fff", border: "2px solid #DAB463", boxShadow: "0 8px 40px -8px rgba(41,95,90,0.12)" }}
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: "rgba(41,95,90,0.08)" }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#295F5A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              </div>
              <p className="text-sm font-semibold uppercase tracking-wider mb-2" style={{ color: "#295F5A" }}>
                Solicita tu presupuesto por email
              </p>
              <a
                href="mailto:presupuestos@reformasoscaralaminos.es"
                className="text-base md:text-lg font-bold break-all transition-colors hover:opacity-75"
                style={{ color: "#295F5A" }}
              >
                presupuestos@<wbr />reformasoscaralaminos.es
              </a>
              <p className="text-muted-foreground text-xs mt-3 leading-relaxed">
                Escríbenos indicando el <strong>tipo de trabajo</strong> que necesitas y tu <strong>número de contacto</strong>. Te enviamos presupuesto sin compromiso en menos de 24 h.
              </p>
              <motion.a
                href="mailto:presupuestos@reformasoscaralaminos.es"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-sm uppercase tracking-wide"
                style={{ background: "#295F5A", color: "#fff" }}
              >
                Enviar email →
              </motion.a>
            </div>

            {/* Tipos de trabajo */}
            <div
              className="rounded-2xl p-6"
              style={{ background: "#fff", border: "1px solid rgba(41,95,90,0.12)", boxShadow: "0 4px 20px -4px rgba(41,95,90,0.08)" }}
            >
              <p className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: "#295F5A" }}>
                Tipo de reparación o reforma
              </p>
              <div className="grid grid-cols-2 gap-2">
                {tiposServicio.map((t) => (
                  <div
                    key={t.label}
                    className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-xs font-semibold"
                    style={{ background: "rgba(41,95,90,0.06)", color: "#295F5A" }}
                  >
                    <span>{t.icon}</span>
                    <span>{t.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                Indícanos en el email cuál es tu caso y adjunta fotos si las tienes.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

// ─── REMOVED FORM — legacy types kept to avoid import errors ─────────────────
function _unusedFormPlaceholder() {
  const [_formState] = useState({ name: "", phone: "", message: "", cookiesAccepted: false });
  const [_cookieError] = useState(false);
  // The following JSX is intentionally unreachable — kept only to satisfy
  // any stale references during refactor. Safe to delete in future cleanup.
  return (
    <div>
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={_formState.cookiesAccepted}
          onChange={() => {}}
          className="mt-0.5 w-4 h-4 flex-shrink-0 accent-primary cursor-pointer"
        />
        <span className="text-xs text-muted-foreground">legacy placeholder</span>
      </label>
    </div>
  );
}

// ─── Zonas de Actuación ───────────────────────────────────────────────────────
function Zonas() {
  return (
    <section
      aria-label="Zonas de actuación reformas Granada"
      className="py-20"
      style={{ background: "linear-gradient(135deg, #295F5A 0%, #1e4a46 100%)" }}
    >
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={springPresets.gentle}
          className="text-center mb-12"
        >
          <div
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-4"
            style={{ background: "rgba(218,180,99,0.15)", color: "#DAB463", border: "1px solid #DAB463" }}
          >
            Dónde trabajamos
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
            Empresa de reformas en la Costa Tropical de Granada
          </h2>
          <p className="text-white/65 text-base max-w-2xl mx-auto">
            Realizamos reformas integrales y reparaciones urgentes en Motril, Granada capital y todos los municipios de la Costa Tropical. Desplazamiento sin coste en toda la zona.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-w-3xl mx-auto"
        >
          {ZONES.map((zone) => (
            <motion.div
              key={zone.name} variants={staggerItem}
              className="rounded-xl px-4 py-4 text-center"
              style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)" }}
            >
              <p className="text-white font-bold text-sm">{zone.name}</p>
              <p className="text-white/45 text-xs mt-0.5">{zone.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
          viewport={{ once: true }} transition={{ delay: 0.4 }}
          className="text-center text-white/50 text-xs mt-8"
        >
          ¿Tu municipio no aparece? Consúltanos — también trabajamos en zonas limítrofes.
        </motion.p>
      </div>
    </section>
  );
}

// ─── FAQ Section ──────────────────────────────────────────────────────────────
function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section
      id="faq"
      aria-label="Preguntas frecuentes sobre reformas en Motril y Granada"
      className="py-20 bg-background"
    >
      <div className="container mx-auto px-6 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={springPresets.gentle}
          className="text-center mb-12"
        >
          <div
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-4"
            style={{ background: "rgba(41,95,90,0.1)", color: "#295F5A", border: "1px solid #295F5A" }}
          >
            Preguntas frecuentes
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-3">
            Todo lo que necesitas saber sobre reformas en Motril y Granada
          </h2>
          <p className="text-muted-foreground text-base">
            Resolvemos las dudas más habituales sobre nuestros servicios de reforma y reparación en la Costa Tropical.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="space-y-3"
        >
          {FAQS.map((faq, i) => (
            <motion.div
              key={i} variants={staggerItem}
              className="rounded-xl overflow-hidden"
              style={{ border: "1px solid rgba(41,95,90,0.15)" }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between px-6 py-4 text-left font-semibold text-sm cursor-pointer bg-white hover:bg-muted/30 transition-colors"
                style={{ color: "#295F5A" }}
                aria-expanded={openIndex === i}
              >
                <span>{faq.q}</span>
                <motion.span
                  animate={{ rotate: openIndex === i ? 180 : 0 }}
                  transition={springPresets.snappy}
                  className="flex-shrink-0 ml-4"
                  style={{ color: "#DAB463" }}
                >
                  <ChevronDown size={18} />
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={springPresets.gentle}
                    className="overflow-hidden"
                  >
                    <p className="px-6 py-4 text-sm text-muted-foreground leading-relaxed border-t"
                      style={{ borderColor: "rgba(41,95,90,0.1)", background: "rgba(41,95,90,0.03)" }}
                    >
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="py-10 text-center text-sm" style={{ background: "#1e4a46", color: "rgba(255,255,255,0.5)" }}>
      <img src="/images/logo.png" alt="Logo" className="h-14 w-auto object-contain mx-auto mb-4 opacity-80" />
      <p className="mb-1 text-white/70 font-medium">{CONTACT.name}</p>
      <p className="mb-1">{CONTACT.address}</p>
      <p className="mb-4">
        <a href="tel:623624182" className="hover:text-accent transition-colors font-mono">{CONTACT.phone}</a>
        {" · "}
        <a href={`https://${CONTACT.web}`} className="hover:text-accent transition-colors">{CONTACT.web}</a>
      </p>
      <div className="h-px w-24 mx-auto mb-4" style={{ background: "rgba(218,180,99,0.4)" }} />
      {/* Legal links */}
      <div className="flex justify-center gap-6 mb-4 text-xs" style={{ color: "rgba(255,255,255,0.45)" }}>
        <Link to="/aviso-legal" className="hover:text-accent transition-colors underline">Aviso Legal</Link>
        <Link to="/politica-cookies" className="hover:text-accent transition-colors underline">Política de Cookies</Link>
      </div>
      <p className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
        © {new Date().getFullYear()} {CONTACT.name} · Todos los derechos reservados
      </p>
      <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.35)" }}>
        Diseño Web{" "}
        <a
          href="https://www.movilizatic.net"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-accent transition-colors font-semibold"
          style={{ color: "#71C2BB" }}
        >
          Movilizatic
        </a>
      </p>
    </footer>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Index() {
  return (
    <div className="min-h-screen font-sans">
      <CookieBanner />
      <Header />
      <Hero />
      <Services />
      <WhyUs />
      <InsuranceSection />
      <Zonas />
      <Contact />
      <FAQ />
      <Footer />
    </div>
  );
}
